import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getServicesQuery } from '@/api/booking/getServicesQuery';
import { rebookAppointmentMutation } from '@/api/booking/rebookAppointmentMutation';
import { toast } from '@/components/_shared/Toaster';
import { APPOINTMENT_LOOKUP_QUERY_KEY } from '@/hooks/queries/appointment/useAppointmentLookupQuery';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { Logger } from '@/utils/logging';

import { isCanNotRebookError } from './types';
import type { UseRebookMutationOptions } from './types';

/**
 * React Query mutation hook for rebooking appointments
 *
 * This hook calls the /v1/booking/{id}/rebook endpoint to create a cart
 * with pre-selected barber and services from a past appointment.
 *
 * The backend handles service ID resolution, ensuring the cart is created with
 * valid service IDs that match the barber's current assignedServices.
 *
 * Features:
 * - Creates cart from past appointment via dedicated rebook endpoint
 * - Backend handles service ID resolution
 * - Error handling with user feedback
 * - Detects barber-unavailable errors and triggers navigation callback
 *
 * Note: Navigation and cache updates should be handled in the component's onSuccess callback.
 *
 * @param options - Mutation configuration options
 * @returns Mutation result with cart data and state
 *
 * @example
 * ```typescript
 * const rebookMutation = useRebookMutation({
 *   onSuccess: (cart) => {
 *     // Update cache and navigate
 *     queryClient.setQueryData(['cart', shopId, cart.id], cart);
 *     router.push(routes.chooseTime({ shopId, barberId, cart: cart.id }));
 *   },
 *   onBarberUnavailable: () => {
 *     router.push(routes.chooseProfessional({ shopId }));
 *   },
 * });
 *
 * // Usage in component
 * rebookMutation.mutate({ appointmentId: 'past-appointment-123' });
 * ```
 */
export const useRebookMutation = (options: UseRebookMutationOptions = {}) => {
  const { t } = useScreenTranslations('AppointmentDetailActions');
  const queryClient = useQueryClient();
  const {
    onBarberUnavailable,
    onServiceUnavailable,
    onSuccess: callerOnSuccess,
    onError: callerOnError,
    ...restMutationOptions
  } = options;

  return useMutation(
    rebookAppointmentMutation({
      onSuccess: async (cart, variables, context, mutationContext) => {
        // Invalidate appointment lookup query to refetch appointments in home screen
        mutationContext.client.invalidateQueries({
          queryKey: APPOINTMENT_LOOKUP_QUERY_KEY,
        });

        const itemsWithServices = cart.items.filter(
          (item) => item.services.length > 0,
        );

        if (itemsWithServices.length > 0) {
          try {
            // Validate each cart item against ITS OWN barber's service list — a
            // group rebook can hold items booked with different barbers, so a
            // single shared list would wrongly flag another barber's service as
            // unavailable. The shop-level `/service` endpoint also returns [] for
            // barbers whose services are only exposed barber-scoped (e.g.
            // booking-code-gated barbers), so scope by barber; any-barber items
            // keep the shop-level list (SQR-14002).
            const buildAvailableIds = (services: { id: string }[]) =>
              new Set(services.map((s) => s.id));

            const availableIdsByBarber = new Map<
              string | undefined,
              Set<string>
            >();
            const getAvailableIds = async (barberId: string | undefined) => {
              const cached = availableIdsByBarber.get(barberId);
              if (cached) return cached;
              const { services } = await queryClient.fetchQuery(
                getServicesQuery(cart.shopId, barberId),
              );
              const ids = buildAvailableIds(services);
              availableIdsByBarber.set(barberId, ids);
              return ids;
            };

            for (const item of itemsWithServices) {
              const barberId = item.bookedWithAnyBarber
                ? undefined
                : (item.barberId ?? undefined);
              const availableIds = await getAvailableIds(barberId);
              const hasUnavailableService = item.services.some(
                (s) => !availableIds.has(s.id),
              );

              if (hasUnavailableService) {
                toast({
                  variant: 'error',
                  message: t('serviceUnavailable'),
                });
                onServiceUnavailable?.();
                return;
              }
            }
          } catch (error) {
            // The availability check itself failed (e.g. network error). Don't
            // fall through to callerOnSuccess — proceeding would navigate the
            // user forward with a cart we couldn't validate. Surface a rebook
            // error and stop (this is a check failure, not a genuine
            // service-unavailable case, so we don't use serviceUnavailable copy).
            Logger.error('[REBOOK] Failed to validate service availability', {
              error,
            });
            toast({
              variant: 'error',
              message: t('rebookError'),
              description: t('rebookErrorDescription'),
            });
            return;
          }
        }

        callerOnSuccess?.(cart, variables, context, mutationContext);
      },
      onError: (error, variables, context, mutationContext) => {
        Logger.error('[REBOOK] Failed to create rebook cart', {
          error: error.message,
          appointmentId: variables.appointmentId,
        });

        if (isCanNotRebookError(error)) {
          toast({
            variant: 'error',
            message: t('barberUnavailable'),
          });
          onBarberUnavailable?.();
          return;
        }

        toast({
          variant: 'error',
          message: t('rebookError'),
          description: t('rebookErrorDescription'),
        });

        callerOnError?.(error, variables, context, mutationContext);
      },
      ...restMutationOptions,
    }),
  );
};
